import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PersonaShell from './persona-shell';
import Spinner from '../../components/common/spinner';
import { useAllClientSummaries, ClientSummary } from '../../hooks/useClients';
import { usePersonaClients, useUpdatePersonaClients } from '../../hooks/usePersonaAdmin';

const PersonaClientsPage: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();
  const { data: assigned, isLoading: assignedLoading } = usePersonaClients(personaId);
  const { data: clients, isLoading: clientsLoading } = useAllClientSummaries(false);
  const updateAssignments = useUpdatePersonaClients(personaId);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (assigned?.client_ids) setSelected(new Set(assigned.client_ids));
  }, [assigned]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredClients = useMemo(() => {
    const list: ClientSummary[] = clients || [];
    if (!search.trim()) return list;
    const term = search.trim().toLowerCase();
    return list.filter((c) => c.name.toLowerCase().includes(term));
  }, [clients, search]);

  const initialSet = useMemo(() => new Set(assigned?.client_ids || []), [assigned]);
  const isDirty = useMemo(() => {
    if (initialSet.size !== selected.size) return true;
    let dirty = false;
    selected.forEach((id) => {
      if (!initialSet.has(id)) dirty = true;
    });
    return dirty;
  }, [initialSet, selected]);

  const onSave = () => updateAssignments.mutate(Array.from(selected));
  const onReset = () => setSelected(new Set(assigned?.client_ids || []));

  return (
    <PersonaShell personaId={personaId} tab="clients">
      <div className="mx-auto max-w-4xl space-y-4 p-8">
        <div className="rounded-md border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
          Members only see a Peersona if their client is assigned here. This is the visibility gate — it does not affect
          publish status.
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Assigned Clients</h2>
            <p className="text-xs text-gray-500">
              {selected.size} selected · {clients?.length ?? 0} total
            </p>
          </div>
          <div className="flex gap-2">
            <button
              className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              onClick={onReset}
              disabled={!isDirty || updateAssignments.isLoading}
            >
              Reset
            </button>
            <button
              className="bg-blue-primary rounded px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={onSave}
              disabled={!isDirty || updateAssignments.isLoading}
            >
              {updateAssignments.isLoading ? 'Saving…' : 'Save Assignments'}
            </button>
          </div>
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter clients…"
          className="w-full rounded border border-gray-300 p-2 text-sm focus:border-transparent focus:ring-2 focus:ring-blue-500"
        />

        {(assignedLoading || clientsLoading) && !clients ? (
          <Spinner alignmentClass="flex items-center justify-center py-16" />
        ) : (
          <div className="rounded-md border border-gray-200 bg-white shadow-sm">
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full table-fixed border-collapse text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <th className="w-10 py-2 pl-4"></th>
                    <th className="py-2">Client</th>
                    <th className="w-32 py-2">Status</th>
                    <th className="w-32 py-2">Members</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-xs text-gray-400">
                        No clients match this filter.
                      </td>
                    </tr>
                  )}
                  {filteredClients.map((client) => {
                    const id = client.id;
                    const checked = selected.has(id);
                    return (
                      <tr key={id} className="cursor-pointer border-b hover:bg-gray-50" onClick={() => toggle(id)}>
                        <td className="py-2 pl-4">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="py-2">
                          <div className="font-medium text-gray-900">{client.name}</div>
                          {client.reseller && <div className="text-xs text-gray-500">{client.reseller}</div>}
                        </td>
                        <td className="py-2 text-xs text-gray-600">{client.status}</td>
                        <td className="py-2 text-xs text-gray-600">{client.total_active_members ?? 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PersonaShell>
  );
};

export default PersonaClientsPage;
